import type { Carer } from '@lmc/ts-common'
import { useEffect, useMemo, useState } from 'react'

export const DEFAULT_PAGE_SIZE = 15

type UsePagedCarersResult = {
  pagedCarers: Carer[]
  currentPage: number
  totalPages: number
  setPage: (page: number) => void
}

// Client-side pagination over an already-filtered/sorted carer list. The page
// resets to 1 whenever `resetKey` changes (caller wires this to tab / search /
// filter / sort state). The hook clamps invalid page numbers so external
// navigations never put us off the end of the list.
const usePagedCarers = (
  carers: Carer[],
  resetKey: string,
  pageSize: number = DEFAULT_PAGE_SIZE
): UsePagedCarersResult => {
  const [currentPage, setCurrentPage] = useState(1)

  // resetKey is a serialised cache-buster that collapses all upstream
  // filter/sort/tab state into one string; it is intentionally the only dep.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    setCurrentPage(1)
  }, [resetKey])

  const totalPages = Math.max(1, Math.ceil((carers?.length ?? 0) / pageSize))

  const safePage = Math.min(Math.max(1, currentPage), totalPages)

  const pagedCarers = useMemo(() => {
    const start = (safePage - 1) * pageSize
    return (carers ?? []).slice(start, start + pageSize)
  }, [carers, safePage, pageSize])

  return {
    pagedCarers,
    currentPage: safePage,
    totalPages,
    setPage: (page: number) =>
      setCurrentPage(Math.min(Math.max(1, page), totalPages))
  }
}

export default usePagedCarers
